import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../config/axios';
import { Bell } from 'lucide-react';
import './ChatBell.css';

const POLL_INTERVAL_MS = 30000;

function ChatBell({ onSelectConversation, buttonClassName, iconSize, userRole, refreshTrigger }) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const containerRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/tenant/chat/notifications/count');
      setCount(res.data.count || 0);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchCount]);

  useEffect(() => {
    if (refreshTrigger != null) fetchCount();
  }, [refreshTrigger, fetchCount]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axiosInstance.get('/api/tenant/chat/conversations')
      .then(res => setConversations(res.data.conversations || []))
      .catch(() => setConversations([]))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [open]);

  const handleSelect = (c) => {
    const name = c.otherParticipant?.displayName || 'Unknown';
    onSelectConversation(c.id, name);
    setOpen(false);
  };

  const handleClearAll = useCallback(async () => {
    const n = conversations.length;
    if (n === 0) return;
    if (!window.confirm(`Delete all ${n} conversation${n !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setClearingAll(true);
    try {
      for (const c of conversations) {
        await axiosInstance.delete(`/api/tenant/chat/conversations/${c.id}`);
      }
      setConversations([]);
      setCount(0);
      setOpen(false);
    } catch (_) {}
    finally {
      setClearingAll(false);
    }
  }, [conversations]);

  return (
    <div className="chat-bell-wrap" ref={containerRef}>
      <button type="button" className={buttonClassName || 'chat-bell-btn'} onClick={() => setOpen(o => !o)} aria-label="Notifications">
        <Bell size={iconSize || 20} />
        {count > 0 && <span className="chat-bell-badge">{count > 99 ? '99+' : count}</span>}
      </button>
      {open && (
        <div className="chat-bell-dropdown">
          {loading ? (
            <div className="chat-bell-dropdown-loading">Loading...</div>
          ) : (
            <>
              {(userRole === 'admin' || userRole === 'manager') && conversations.length > 0 && (
                <div className="chat-bell-dropdown-header">
                  <button type="button" className="chat-bell-clear-all" onClick={handleClearAll} disabled={clearingAll}>
                    {clearingAll ? 'Clearing...' : 'Clear all'}
                  </button>
                </div>
              )}
              {conversations.length === 0 ? (
                <div className="chat-bell-dropdown-empty">No conversations</div>
              ) : (
                conversations.map(c => (
                  <button key={c.id} type="button" className="chat-bell-dropdown-item" onClick={() => handleSelect(c)}>
                    <span className="chat-bell-dropdown-name">{c.otherParticipant?.displayName || 'Unknown'}</span>
                    {c.lastMessage && <span className="chat-bell-dropdown-preview">{c.lastMessage.body || '(attachment)'}</span>}
                    {c.unreadCount > 0 && <span className="chat-bell-dropdown-unread">{c.unreadCount}</span>}
                  </button>
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ChatBell;
